import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';
import { RequestObjectBase } from './_base.js';
import { enforceContainerDictionaryInvariants } from './container-dictionary-refine.js';
import { RequestDestinationInputSchema } from './destinations.js';
import {
  RequestPackagingOptionIdsInputSchema,
  RequestRoadOptionIdsInputSchema,
} from './dictionary-selections.js';
import { CreateRequestItemInputSchema } from './items.js';
import {
  isContainerKind,
  isSimpleKind,
  kindForbidsInsurance,
  RequestKindSchema,
  type RequestKind,
} from './kind.js';
import { CarrierBidCurrencySchema } from './status.js';

// WHY (#1052): both-or-neither cargo value/currency invariant for the
//   write path. CREATE applies this strictly (no prior state to merge);
//   PATCH applies it only when `insuranceRequested` is supplied in the
//   body (the service merges against the persisted row otherwise).
type InsuranceRefineInput = {
  readonly insuranceRequested?: boolean | undefined;
  readonly insuranceCargoValue?: number | null | undefined;
  readonly insuranceCargoCurrency?: string | null | undefined;
};
const enforceInsuranceInvariants = (
  body: InsuranceRefineInput,
  ctx: z.RefinementCtx,
  opts: { requireWhenTrue: boolean },
): void => {
  if (body.insuranceRequested === true) {
    if (opts.requireWhenTrue) {
      if (body.insuranceCargoValue === undefined || body.insuranceCargoValue === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'insuranceCargoValue required when insuranceRequested is true',
          path: ['insuranceCargoValue'],
        });
      }
      if (body.insuranceCargoCurrency === undefined || body.insuranceCargoCurrency === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'insuranceCargoCurrency required when insuranceRequested is true',
          path: ['insuranceCargoCurrency'],
        });
      }
    }
  } else if (body.insuranceRequested === false) {
    if (body.insuranceCargoValue !== undefined && body.insuranceCargoValue !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'insuranceCargoValue must be null when insuranceRequested is false',
        path: ['insuranceCargoValue'],
      });
    }
    if (body.insuranceCargoCurrency !== undefined && body.insuranceCargoCurrency !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'insuranceCargoCurrency must be null when insuranceRequested is false',
        path: ['insuranceCargoCurrency'],
      });
    }
  }
};

// WHY: `manager` is the actor/owner identity for a request and must be
// derived server-side from the authenticated session, not the client body.
// Including it here would let any future consumer both create AND reassign
// a request's manager via PATCH (PatchRequestInputSchema partializes this
// shape) without an RBAC-gated path or audit semantics. Reassignment
// belongs on a dedicated, permission-gated endpoint with explicit audit
// emission, not as a free-form patch field.
//
// WHY (#1447, #2116): top-level `weight`/`volume` are WRITABLE on create for
// STANDARD/CONTAINER request-level totals (the omit below strips the
// read-aggregate keys; the `.extend` re-adds them as nullable writable
// inputs). For SIMPLE the service ignores them and the read path keeps the
// item/box-derived aggregate, so a stray value cannot drift it.
//
// INVARIANT: `RequestItem.id` is server-minted on insert (Postgres
// sequence `request_item_seq`, migration 20260505040000). The wire
// `RequestItemSchema.id` field round-trips on the response, but the
// CREATE input must NOT carry one — a client-supplied id would either
// collide globally (P2002) or silently get overwritten by the
// sequence default. Override `items` with `RequestItemSchema.omit({
// id: true })` so the input layer rejects ids consistently.
// WHY (#701): cross-field invariants for the kind discriminator. Lives
// here so both `CreateRequestInputSchema` and `PatchRequestInputSchema`
// can reuse the same checks (with patch-aware nullability). Reads
// `kind` / `transportTypeId` / `items` from the parsed body; trusts the
// `boxes` field on the body alone (the create input does not carry
// boxes — the draft-save path owns the boxes invariant separately).
type KindRefineInput = {
  readonly kind: RequestKind;
  readonly transportTypeId?: string | null | undefined;
  readonly items?: ReadonlyArray<{ weight: number | null; volume: number | null }> | undefined;
  // WHY (#1053): container-only delivery address. The wire-level
  //   create/patch invariants reject (a) STANDARD carrying a non-null
  //   value and (b) CONTAINER with a missing/empty value on create.
  //   `destinations` is the STANDARD-side multiselect — the
  //   acceptance criterion «CONTAINER write paths reject destination
  //   IDs» is enforced here as «destinations must be omitted on
  //   CONTAINER create».
  readonly deliveryAddress?: string | null | undefined;
  // WHY (#1392, #2584): pickup address is writable for SIMPLE and
  //   CONTAINER. The create/patch invariants reject (a) STANDARD carrying
  //   a non-null value and (b) CONTAINER with a missing/empty value on create. Unlike
  //   `deliveryAddress`, it never touches `routeTo` or the read refine.
  readonly pickupAddress?: string | null | undefined;
  readonly destinations?: ReadonlyArray<{ arrivalCityId: string }> | undefined;
  // WHY (#1303): SIMPLE forbids insurance, so the kind refine needs the
  //   insurance flag to reject a SIMPLE request that requests it. The
  //   value/currency both-or-neither rule still lives in
  //   `enforceInsuranceInvariants`.
  readonly insuranceRequested?: boolean | undefined;
};
const enforceKindInvariants = (
  body: KindRefineInput,
  ctx: z.RefinementCtx,
  opts: {
    itemsRequiredOnStandard: boolean;
    deliveryAddressRequiredOnContainer: boolean;
    pickupAddressRequiredOnContainer: boolean;
  },
): void => {
  if (isContainerKind(body.kind)) {
    if (body.transportTypeId === null || body.transportTypeId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request requires transportTypeId',
        path: ['transportTypeId'],
      });
    }
    if (opts.deliveryAddressRequiredOnContainer) {
      if (
        body.deliveryAddress === null ||
        body.deliveryAddress === undefined ||
        body.deliveryAddress.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'container request requires deliveryAddress',
          path: ['deliveryAddress'],
        });
      }
    }
    // WHY (#1392): CONTAINER create requires a non-empty pickupAddress.
    //   The patch path passes `pickupAddressRequiredOnContainer: false`
    //   because a flip to CONTAINER need not ship pickup in the same
    //   body — the service merges against the persisted row.
    if (opts.pickupAddressRequiredOnContainer) {
      if (
        body.pickupAddress === null ||
        body.pickupAddress === undefined ||
        body.pickupAddress.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'container request requires pickupAddress',
          path: ['pickupAddress'],
        });
      }
    }
    // WHY (#1053): CONTAINER write paths refuse to land destination
    //   rows — the carrier scope is request-wide and destinations are
    //   STANDARD-only. The create path strips `destinations` from the
    //   CONTAINER input shape via this gate; the patch path applies the
    //   rule only when `destinations` is supplied in the body.
    if (body.destinations !== undefined && body.destinations.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'container request must not carry destinations',
        path: ['destinations'],
      });
    }
  } else {
    // STANDARD/SIMPLE branch
    if (
      body.destinations !== undefined &&
      body.destinations.length === 0 &&
      opts.itemsRequiredOnStandard
    ) {
      // WHY (#1053): destinations.length >= 1 is required on STANDARD
      //   creates (itemsRequiredOnStandard is the create-side discriminator).
      //   PATCH path skips this; the service-layer reconcile keeps the
      //   persisted destinations untouched when the body omits the key,
      //   and the wire's `enforceActiveStrictness` rejects an active
      //   STANDARD row with zero destinations anyway.
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        path: ['destinations'],
        message: 'standard request requires at least one destination',
      });
    }
    if (body.transportTypeId !== undefined && body.transportTypeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null transportTypeId',
        path: ['transportTypeId'],
      });
    }
    if (body.deliveryAddress !== undefined && body.deliveryAddress !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null deliveryAddress',
        path: ['deliveryAddress'],
      });
    }
    // WHY (#1392, #2584): only STANDARD must carry a null pickupAddress;
    //   SIMPLE now owns the optional origin-address writer.
    if (
      !isSimpleKind(body.kind) &&
      body.pickupAddress !== undefined &&
      body.pickupAddress !== null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'standard request must have null pickupAddress',
        path: ['pickupAddress'],
      });
    }
    // WHY (#701, narrowed by #1447): only SIMPLE creates require non-null
    //   per-item weight/volume. STANDARD moved to a request-level total
    //   (#1447), so its items may carry null — the request-level total is
    //   authoritative and the send gate enforces it.
    if (opts.itemsRequiredOnStandard && isSimpleKind(body.kind) && body.items !== undefined) {
      for (let i = 0; i < body.items.length; i += 1) {
        const it = body.items[i];
        if (!it) continue;
        if (it.weight === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'standard request item requires weight',
            path: ['items', i, 'weight'],
          });
        }
        if (it.volume === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'standard request item requires volume',
            path: ['items', i, 'volume'],
          });
        }
      }
    }
    // WHY (#1303 / #1453): SIMPLE and STANDARD both forbid insurance on the
    //   write path — SIMPLE per #1303, STANDARD added by #1453 after its
    //   insurance UI was removed (#1448). The value/currency both-or-neither
    //   floor stays in `enforceInsuranceInvariants`; here we reject the flag
    //   itself so a SIMPLE/STANDARD create/patch can never request insurance.
    //   CONTAINER is excluded — it still permits insurance.
    if (kindForbidsInsurance(body.kind) && body.insuranceRequested === true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // WHY (#1303): keep the exact SIMPLE wording so SIMPLE consumers and
        //   tests are unchanged; STANDARD gets its own kind-appropriate copy.
        message: isSimpleKind(body.kind)
          ? 'simple request forbids insurance'
          : 'standard request forbids insurance',
        path: ['insuranceRequested'],
      });
    }
  }
};

const CreateRequestInputBase = RequestObjectBase.omit({
  id: true,
  status: true,
  manager: true,
  logist: true,
  managerId: true,
  logistId: true,
  createdAt: true,
  updatedAt: true,
  slaDeadline: true,
  inWorkAt: true,
  closedAt: true,
  // WHY (#960): logistics-completion timestamp is server-derived (set
  //   by the `IN_WORK -> PROCESSED` transition in #961). Same omit
  //   posture as `inWorkAt` / `slaDeadline` / `closedAt`.
  processedAt: true,
  carriers: true,
  winnerBidId: true,
  attachments: true,
  chat: true,
  audit: true,
  returnReason: true,
  refusalReason: true,
  refusalComment: true,
  archivedFromStatus: true,
  weight: true,
  volume: true,
  // WHY (#700): `route.from`/`route.to` are no longer client-supplied
  //   — the server hard-sets `from='Китай'` and denormalises `to` from
  //   the selected destinations. Inputs MUST send `destinations`
  //   instead.
  route: true,
  // WHY (#701): boxes are not part of the create input — they ship via
  //   the draft-save path or the box reconcile endpoint. Strip them so
  //   a tampered POST cannot land box rows on the create path (and so
  //   the CONTAINER no-boxes invariant has nothing to fight here).
  boxes: true,
  // overridden below with defaults so callers can omit `kind` and
  // produce the historical STANDARD shape.
  kind: true,
  transportTypeId: true,
  // WHY (#996): roads/packaging are projected read-only on the response
  //   shape (Array<{ id, name, active, sortOrder }>) — the create input
  //   carries plain UUID arrays via the `roadOptionIds`/`packagingOptionIds`
  //   fields below. Strip the projected response keys so the input layer
  //   rejects them and the request body stays canonical.
  roads: true,
  packaging: true,
  // WHY (#1596): `leadPublicCode`/`dealPublicCode` are a response-only
  //   projection (the `L-#####`/`D-#####` of the linked Lead/Deal, surfaced
  //   only via `?includeFunnelLinks=true`). They are NEVER a create input —
  //   the link is set via `leadId`/`dealId`. Strip them so the strict input
  //   rejects the keys instead of silently dropping them.
  leadPublicCode: true,
  dealPublicCode: true,
})
  .extend({
    // INVARIANT (#521): the create path stays strict — non-empty header
    // `name`, strict per-item invariants. Partial DRAFT values may only
    // land via `POST /requests/draft` / `PUT /requests/:id/draft`.
    // Re-applying these constraints here keeps `CreateRequestInputSchema`
    // a true subset of pre-#521 strictness even though the underlying
    // response shape now relaxes them.
    name: z.string().min(1),
    // WHY (#701): default STANDARD so pre-#701 callers (and tests) that
    //   omit `kind` keep producing a standard request.
    kind: RequestKindSchema.default('STANDARD'),
    // WHY (#701): nullable + default null so STANDARD callers can omit
    //   it. CONTAINER callers must supply a UUID — enforced by the
    //   cross-field refine below.
    transportTypeId: z.string().uuid().nullable().default(null),
    items: z.array(CreateRequestItemInputSchema),
    // WHY (#700 / #1053): canonical source for the request's arrival
    //   cities. STANDARD requires `min(1)` (enforced by
    //   `enforceKindInvariants` below — same body refine that rejects
    //   CONTAINER carrying any). CONTAINER must carry zero (the
    //   carriers are request-wide); the default `[]` lets a CONTAINER
    //   caller omit the key.
    destinations: z.array(RequestDestinationInputSchema).default([]),
    // WHY (#996): request-level multiselects for Roads and Packaging.
    //   Both default to `[]` so pre-#996 callers (and tests) keep
    //   working without listing the keys. The service-layer write path
    //   validates every id exists AND is active. These are independent
    //   of `routeFrom`/`destinations` — Roads/Packaging coexist with the
    //   route fields and do not replace them.
    roadOptionIds: RequestRoadOptionIdsInputSchema.default([]),
    packagingOptionIds: RequestPackagingOptionIdsInputSchema.default([]),
    // WHY (#1088): the read shape requires `dealId` (mirror of
    //   `leadId`), but the create path defaults to null so pre-#1088
    //   callers and fixtures that omit the key keep working. Tests that
    //   explicitly send a Deal id at create time still parse.
    dealId: z.string().uuid().nullable().default(null),
    // WHY (#1052): China-only insurance fields on the create path. All
    //   three default to the "not requested" stance (false / null / null)
    //   so pre-#1052 callers — and tests that don't list the keys — keep
    //   working. The cross-field refine below enforces both-or-neither.
    insuranceRequested: z.boolean().default(false),
    insuranceCargoValue: z.number().positive().nullable().default(null),
    insuranceCargoCurrency: CarrierBidCurrencySchema.nullable().default(null),
    // WHY (#1447, #2116): STANDARD/CONTAINER request-level total weight (kg)
    //   + volume (m³). The response shape exposes `weight`/`volume` as
    //   recomputed read-only aggregates (stripped from the create input
    //   above); #1447 re-introduced them as WRITABLE inputs for STANDARD and
    //   #2116 widened persistence to CONTAINER (both carry a single
    //   request-level total). Nullable + default null so SIMPLE and
    //   pre-backfill callers that omit the keys keep working — the service
    //   persists them for STANDARD and CONTAINER and the read path falls back
    //   to the item/box-derived total when the column is null.
    weight: z.number().nonnegative().nullable().default(null),
    volume: z.number().nonnegative().nullable().default(null),
    // WHY (#1053): container-only delivery address. STANDARD callers
    //   omit the key (defaults to null), CONTAINER callers must supply
    //   a non-empty string — enforced by `enforceKindInvariants` below.
    //   `.trim()` collapses whitespace-only values so the persisted row
    //   always matches the wire's «non-empty» definition.
    deliveryAddress: z
      .string()
      .max(2000)
      .nullable()
      .default(null)
      .transform((value) => (value === null ? null : value.trim())),
    // WHY (#1392, #2584): SIMPLE callers may supply this optional origin;
    //   STANDARD callers keep null and CONTAINER callers must supply a
    //   non-empty string. Blank input canonicalizes to null.
    pickupAddress: z
      .string()
      .max(2000)
      .nullable()
      .default(null)
      .transform((value) => (value === null ? null : value.trim() || null)),
    // WHY: client-minted idempotency token for the response-loss-on-
    // retry duplicate-create bug (#415). INPUT-only on this slice —
    // the backend stores it and resolves retries against it but does
    // NOT echo it on `RequestSchema` responses, so the wire-skew
    // direction "new backend serves old strict client" stays safe
    // (CLAUDE.md expand-and-contract for wire-breaking changes). The
    // frontend already knows the value it minted; the response does
    // not need to round-trip it for the dedup contract to work.
    //
    // INVARIANT: lowercase canonicalization is mandatory and lives
    // here, not in the service. Postgres TEXT is case-sensitive but
    // RFC 4122 UUIDs are case-insensitive — without canonicalization a
    // case-flipping retry (e.g. uppercase from a different platform's
    // crypto.randomUUID() implementation) would store as a DISTINCT
    // value, miss the unique index, and create the duplicate the
    // dedup contract is meant to prevent. The .transform() runs at
    // Zod parse time so by the moment the value reaches the service
    // it is already in canonical form.
    //
    // Optional because pre-#415 client builds and any non-frontend
    // caller (server-to-server, future API consumer) that does not
    // need idempotency should be able to omit the field. The partial
    // UNIQUE INDEX excludes NULL rows so callers who omit are not
    // subject to dedup constraints.
    clientToken: z
      .string()
      .uuid('clientToken must be a valid RFC 4122 UUID')
      .transform((s) => s.toLowerCase())
      .optional(),
  })
  .strict();

export const CreateRequestInputSchema = CreateRequestInputBase.superRefine((body, ctx) => {
  enforceKindInvariants(body, ctx, {
    itemsRequiredOnStandard: true,
    deliveryAddressRequiredOnContainer: true,
    pickupAddressRequiredOnContainer: true,
  });
  enforceInsuranceInvariants(body, ctx, { requireWhenTrue: true });
  // WHY (#1446): CONTAINER create rejects a non-empty Roads/Packaging
  //   selection — the redesigned CONTAINER form no longer carries those
  //   selectors. Empty / omitted stays accepted (default `[]` above).
  enforceContainerDictionaryInvariants(body, ctx);
});
export type CreateRequestInput = z.infer<typeof CreateRequestInputSchema>;

// INVARIANT: PATCH targets the request HEADER only — items, weight,
// volume, and any other aggregate are mutated through dedicated
// sub-resource endpoints (`/items/*`, etc.). Reusing the partial of
// CreateRequestInputSchema would silently accept `items` on a parent
// PATCH and then drop it in the service (the same silent-drop pattern
// the create input now guards against). A purpose-built header schema
// keeps the patch surface tiny and makes contract drift a compile-time
// error: adding a new patchable field requires both a Zod entry here
// and a service branch that writes it and emits an audit row.
const PatchRequestInputBaseShape = z
  .object({
    name: z.string().min(1),
    // WHY (#700): replaces the previous `route` field on the patch
    //   header. `destinations` is optional: omission preserves the existing
    //   rows, while `[]` lets a kind-flip body repeat an already-empty set.
    //   Service and status invariants reject writes that would newly land a
    //   request whose kind/status requires meaningful destinations on empty.
    destinations: z.array(RequestDestinationInputSchema),
    // WHY (#1077): same tightening as `RequestObjectShape.leadId` — the
    //   patch input must reject non-UUID strings now that the DB column
    //   is `@db.Uuid` and FK-bound to `Lead`.
    leadId: z.string().uuid().nullable(),
    // WHY (#1088): commercial Deal link mirror of `leadId` (#1087 added
    //   the FK; #1088 wires the PATCH slot). Visibility on the target
    //   Deal is gated server-side via `assertDealVisibleForLink`;
    //   `DEAL_REQUEST_LINKED` / `DEAL_REQUEST_UNLINKED` activity rows
    //   fire on the affected deals when this slot moves.
    dealId: z.string().uuid().nullable(),
    // WHY (#701): kind / transportTypeId may be patched together. The
    //   refine below rejects mismatched pairs; the service rejects a
    //   kind flip when the row already has child rows that the target
    //   kind forbids (e.g. flipping STANDARD→CONTAINER on a row that
    //   already has `RequestBox` rows). Both keys MUST appear together
    //   or together — partial-only updates can omit them.
    kind: RequestKindSchema,
    transportTypeId: z.string().uuid().nullable(),
    // WHY (#996): replace-the-whole-set semantics — when present, the
    //   array is authoritative and the service replaces the join rows.
    //   Omitted = preserve current selections. The service tolerates
    //   inactive ids that were ALREADY part of the prior selection but
    //   rejects new inactive ids.
    roadOptionIds: RequestRoadOptionIdsInputSchema,
    packagingOptionIds: RequestPackagingOptionIdsInputSchema,
    // WHY (#1052): patchable insurance triplet. All three are optional —
    //   the service merges with the persisted row to apply the
    //   both-or-neither invariant. Wire layer enforces the structural
    //   floor and the cross-field rule that applies when the flag is
    //   present in the body (see `enforceInsuranceInvariants` below).
    insuranceRequested: z.boolean(),
    insuranceCargoValue: z.number().positive().nullable(),
    insuranceCargoCurrency: CarrierBidCurrencySchema.nullable(),
    // WHY (#1053): patchable deliveryAddress. Optional via `.partial()`
    //   below — the service merges against the persisted row and applies
    //   the kind-aware invariant there. Trim-on-parse keeps the same
    //   canonical-form contract as create.
    deliveryAddress: z
      .string()
      .max(2000)
      .nullable()
      .transform((value) => (value === null ? null : value.trim())),
    // WHY (#1392, #2584): patchable pickupAddress. Optional via `.partial()`
    //   below — the service merges against the persisted row and applies
    //   the kind-aware invariant there. Trim-on-parse keeps the same
    //   canonical-form contract as create.
    pickupAddress: z
      .string()
      .max(2000)
      .nullable()
      .transform((value) => (value === null ? null : value.trim() || null)),
  })
  .partial()
  .strict();
export const PatchRequestInputSchema = nonEmptyPatch(
  PatchRequestInputBaseShape.superRefine((body, ctx) => {
    // WHY (#701): the patch is only required to satisfy the kind ↔
    //   transportTypeId pair when both keys are present (or when one is
    //   present alongside an inferable counterpart). Since the patch
    //   body alone cannot know the row's CURRENT kind, we apply the
    //   strict pair check only when `kind` is supplied. Otherwise the
    //   service-layer invariant gates against the persisted row.
    if (body.kind !== undefined) {
      enforceKindInvariants(
        {
          kind: body.kind,
          transportTypeId: body.transportTypeId,
          items: undefined,
          deliveryAddress: body.deliveryAddress,
          pickupAddress: body.pickupAddress,
          destinations: body.destinations,
          // WHY (#1303): a SIMPLE patch that flips insurance on must be
          //   rejected at the wire; the service merges value/currency.
          insuranceRequested: body.insuranceRequested,
        },
        ctx,
        {
          itemsRequiredOnStandard: false,
          // WHY (#1053): on PATCH, a flip to CONTAINER does not have to
          //   ship the deliveryAddress in the same body — the service
          //   merges against the persisted row. The wire only rejects
          //   the structural «STANDARD-must-have-null» half and the
          //   «destinations-on-CONTAINER» rejection when those keys are
          //   present.
          deliveryAddressRequiredOnContainer: false,
          // WHY (#1392): a flip to CONTAINER does not have to ship
          //   pickupAddress in the same body — the service merges against
          //   the persisted row. Only the structural
          //   «STANDARD-must-have-null» half is enforced at the wire.
          pickupAddressRequiredOnContainer: false,
        },
      );
      // WHY (#1446): when `kind` is present on the patch body the wire
      //   knows the target kind, so reject a non-empty Roads/Packaging
      //   selection against a CONTAINER target here. The kind-omitted
      //   path cannot know the persisted kind — the service-layer PATCH
      //   merge enforces it against the persisted row there.
      enforceContainerDictionaryInvariants(
        {
          kind: body.kind,
          roadOptionIds: body.roadOptionIds,
          packagingOptionIds: body.packagingOptionIds,
        },
        ctx,
      );
    } else if (
      body.deliveryAddress !== undefined ||
      body.pickupAddress !== undefined ||
      body.destinations !== undefined
    ) {
      // WHY (#1053): kind is omitted but a kind-discriminated key was
      //   sent; the service merges against the persisted row's `kind`
      //   to apply the rule. Wire-level: the body is structurally
      //   valid (nullable string / array of FK rows); the cross-field
      //   check lives at the service layer.
    }
    // WHY (#1052): if the patch sets `insuranceRequested = true` it must
    //   also be paired with a non-null cargoValue + currency in the same
    //   body OR the service-layer merge sees a persisted row that
    //   already has them. The wire only enforces the "false → both null"
    //   case strictly (a `requested: false` body must not carry non-null
    //   value/currency); the "true requires both" case is enforced by
    //   the service so a flag-only patch is accepted when the existing
    //   row already carries the value/currency.
    enforceInsuranceInvariants(body, ctx, { requireWhenTrue: false });
  }),
);
export type PatchRequestInput = z.infer<typeof PatchRequestInputSchema>;
